import React from 'react'
import {ReactComponent as ShapeBlob} from '../../../images/shapes/blurs/blur-1.svg'
import {ReactComponent as DividerCurve} from '../../../images/shapes/curves/curve-2.svg'
import {ReactComponent as BottomDivider} from '../../../images/shapes/curves/curve-1.svg'
import dashboardSketchImg from '../../../images/homepage/coliberty-dashboard-icon.png'
import {Case, Assessment, Tasks, Users, UserShield, Form} from '../../../main/icons'
import {IsNotLoggedIn} from '../../../auth/AccessConditionals'
import {useAuthContext} from '../../../auth/AuthContextProvider'
import {FormattedMessage} from 'react-intl'

function FeatureCard({svgIcon: SvgComponent, title, text}) {
  return (
    <div className="card border-white mb-5 shadow-sm rounded">
      <div className="card-header bg-transparent border-light text-center p-5">
        <SvgComponent height={50} className="svg-gradient-success" />
      </div>
      <div className="card-body">
        <h4 className="font-weight-lighter">{title}</h4>
        <p className="text-muted mb-0">{text}</p>
      </div>
    </div>
  )
}

export function HomePageContent() {
  const {loginWithRedirect} = useAuthContext()

  return (
    <>
      <section className="position-relative py-4 py-md-5 mb-5">
        <div className="shape shape-fluid-x shape-blur-1 svg-shim text-light">
          <ShapeBlob />
        </div>
        <div className="container">
          <div className="row align-items-center">
            <div className="col-12 col-md-6 order-md-2">
              <div className="img-skewed img-skewed-left mb-8 mb-md-0 p-1">
                <img
                  src={dashboardSketchImg}
                  alt="Dashboard preview sketch"
                  className="screenshot img-fluid mw-md-50 border-primary img-skewed-item-left"
                  style={{
                    borderWidth: 2,
                    borderStyle: 'solid',
                    borderRadius: '1rem'
                  }}
                />
              </div>
            </div>
            <div className="col-12 col-md-6 order-md-1">
              <h1 className="display-4">
                Collaboration <br />
                <span className="text-primary">simplified</span>.
              </h1>

              <p className="lead text-muted mb-6 mb-md-8">
                After sign up you will be able to Request an Authorisation and receive progress updates by email.
              </p>

              <IsNotLoggedIn>
                <button className="btn btn-lg btn-primary lift" onClick={() => loginWithRedirect()}>
                  <FormattedMessage id="general.log_in" /> / <FormattedMessage id="general.sign_up" />
                </button>
              </IsNotLoggedIn>
            </div>
          </div>
        </div>
      </section>

      <section className="container py-4 py-md-5 mb-lg-5">
        <div className="row">
          <div className="col-sm-6 col-md-4 text-center">
            <h3 className="font-weight-lighter">Collaboration</h3>
            <p className="text-muted">
              Invite people to participate and communicate your requirements among peers, add comments and share details
              of your progress.
            </p>
          </div>
          <div className="col-sm-6 col-md-4 text-center">
            <h3 className="font-weight-lighter">Notifications</h3>
            <p className="text-muted">
              Changes you make are notified to interested parties so they can respond back quickly.
            </p>
          </div>
          <div className="col-sm-6 col-md-4 text-center">
            <h3 className="font-weight-lighter">Messaging</h3>
            <p className="text-muted">
              If you are not keen to open a browser then respond back to notifications through email.
            </p>
          </div>
        </div>
      </section>

      <div className="position-relative">
        <div className="shape shape-bottom shape-fluid-x svg-shim text-light">
          <DividerCurve />
        </div>
      </div>
      <div className="bg-light">
        <section className="container py-4 py-md-5 bg-light">
          <h2 className="text-center font-weight-lighter mb-4">Solution features</h2>

          <div className="row">
            <div className="col-sm-6 col-md-4">
              <FeatureCard
                svgIcon={Case}
                title={'Requested Authorisations'}
                text={
                  'Search/filter and collaborate with people to go through required steps for the requested authorisation to be resolved.'
                }
              />
            </div>
            <div className="col-sm-6 col-md-4">
              <FeatureCard
                svgIcon={Users}
                title={'People'}
                text={
                  'Invite people outside of this system and assign tasks for them. Also filter through the list to see who is who.'
                }
              />
            </div>
            <div className="col-sm-6 col-md-4">
              <FeatureCard
                svgIcon={Tasks}
                title={'Tasks'}
                text={
                  'Assign people tasks they need to complete in order for the requested authorisation to move forward. These consist of forms needed to be filled in.'
                }
              />
            </div>
            <div className="col-sm-6 col-md-4">
              <FeatureCard
                svgIcon={Form}
                title={'Forms'}
                text={'Preview digitized forms taken from dol process. Link them in tasks for others to fill.'}
              />
            </div>
            <div className="col-sm-6 col-md-4">
              <FeatureCard
                svgIcon={Assessment}
                title={'Assessment'}
                text={
                  'Allowing users of this application to quickly assess and Request an Authorisation which then admins can verify and work on.'
                }
              />
            </div>
            <div className="col-sm-6 col-md-4">
              <FeatureCard
                svgIcon={UserShield}
                title={'Authentication'}
                text={
                  'Anyone can sign up and become a user of this application, but in order to perform main tasks people need to be assigned an appropriate role by administrator.'
                }
              />
            </div>
          </div>
        </section>

        <div className="position-relative">
          <div className="shape shape-bottom shape-fluid-x svg-shim text-white">
            <BottomDivider />
          </div>
        </div>
      </div>

      <section className="py-4 py-md-5 mb-lg-5">
        <div className="container">
          <div className="row justify-content-between">
            <div className="col-12 col-md">
              <h4 className="font-weight-lighter">For assessors</h4>
              <p className="text-muted">
                After getting notified your input will be collected in a familiar web browser environment. Further
                collaboration with admins is supported by either replying to email notifications or by commenting.
              </p>
            </div>
            <div className="col-1" />
            <div className="col-1 border-left" />
            <div className="col-12 col-md">
              <h4 className="font-weight-lighter">For administrators</h4>
              <p className="text-muted">
                Simple workflow allows quick resolution of requested authorisations, easy task assignments and leads to
                better outcomes with better collaboration support.
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
